import { db } from "../firebaseClient"
import { doc, setDoc, updateDoc, collection, getDocs, query, where, serverTimestamp } from "firebase/firestore"
import { logPlatformEvent } from "./eventHelpers"

// Minimal orders schema — enough to drive real Revenue/Orders/Customers KPIs
// without building the full commerce module yet. Customers are DERIVED from
// orders (distinct customerEmail) for now — same pattern as "Learners" being
// derived from enrollments rather than a dedicated collection.
export const createOrder = async ({
  orgId,
  customerId,
  customerName,
  customerEmail,
  customerType, // "individual" | "organization"
  productId,
  productTitle,
  productType, // "course" | "program" | "path" etc.
  amount,
  currency,
  status, // "paid" | "pending" | "failed" | "refunded"
}) => {
  if (!orgId || !amount || !productId) {
    return { orderId: null, error: { message: "Missing required order fields" } }
  }

  try {
    const orderRef = doc(collection(db, "orders"))
    const orderData = {
      orderId: orderRef.id,
      orgId,
      customerId: customerId || null,
      customerName: customerName || "",
      customerEmail: (customerEmail || "").trim().toLowerCase(),
      customerType: customerType || "individual",
      productId,
      productTitle: productTitle || "",
      productType: productType || "course",
      amount,
      currency: currency || "USD",
      status: status || "pending",
      createdAt: serverTimestamp(),
      paidAt: status === "paid" ? serverTimestamp() : null,
    }
       await setDoc(orderRef, orderData)
    logPlatformEvent({
      type: status === "paid" ? "PAYMENT_COMPLETED" : "ORDER_CREATED",
      actorUid: customerId,
      actorType: "user",
      orgId,
      targetPath: `orders/${orderRef.id}`,
      metadata: { orderId: orderRef.id, productId, productTitle, amount, currency: currency || "USD", status: status || "pending" },
    })
    return { orderId: orderRef.id, error: null }
  } catch (error) {
    return { orderId: null, error }
  }
}

export const updateOrderStatus = async (orderId, status, orgId = null) => {
  try {
    const orderRef = doc(db, "orders", orderId)
    await updateDoc(orderRef, { status, paidAt: status === "paid" ? serverTimestamp() : null })
    logPlatformEvent({
      type: status === "paid" ? "PAYMENT_COMPLETED" : status === "refunded" ? "PAYMENT_REFUNDED" : status === "failed" ? "PAYMENT_FAILED" : "ORDER_STATUS_CHANGED",
      actorType: "system",
      orgId,
      targetPath: `orders/${orderId}`,
      severity: status === "failed" ? "warning" : "info",
      metadata: { orderId, status },
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

const toMillisSafe = (value) => {
  if (!value) return 0
  if (typeof value?.toMillis === "function") return value.toMillis()
  return new Date(value).getTime() || 0
}

// All orders for an org — source for dashboard commerce KPIs + the Orders tab.
export const listOrgOrders = async (orgId) => {
  try {
    const ordersRef = collection(db, "orders")
    const q = query(ordersRef, where("orgId", "==", orgId))
    const snap = await getDocs(q)
    const orders = snap.docs?.map((d) => d.data())
    orders.sort((a, b) => toMillisSafe(b?.createdAt) - toMillisSafe(a?.createdAt))
    return { orders, error: null }
  } catch (error) {
    return { orders: [], error }
  }
}
// Derived display ID — no schema change, no counter/transaction needed.
// #NSL-XXXXXX from the last 6 chars of the Firestore doc ID.
export const formatOrderDisplayId = (orderId) => {
  if (!orderId) return "#NSL-000000"
  return `#NSL-${orderId.slice(-6).toUpperCase()}`
}